/**
 * MediaUnlocker - Cross-route media permission manager for iOS
 * Unlocks media playback with sound by priming the AudioContext on user gesture
 */
export class MediaUnlocker {
  private static _instance: MediaUnlocker;
  private ctx: AudioContext | null = null;
  private unlocked = false;

  static instance() {
    if (!this._instance) {
      this._instance = new MediaUnlocker();
    }
    return this._instance;
  }

  /**
   * Check if media has been unlocked in this session
   */
  isUnlocked(): boolean {
    return this.unlocked || sessionStorage.getItem('media_unlocked') === '1';
  }

  /**
   * Unlock media playback by resuming AudioContext and playing silent audio
   * Must be called within a user gesture event handler
   */
  async unlock(): Promise<void> {
    if (this.isUnlocked()) {
      console.log('Media already unlocked');
      return;
    }

    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    
    if (!AudioContextClass) {
      console.warn('AudioContext not supported, marking as unlocked anyway');
      sessionStorage.setItem('media_unlocked', '1');
      this.unlocked = true;
      return;
    }

    try {
      // Create or reuse AudioContext
      this.ctx = this.ctx || new AudioContextClass();
      
      // Resume if suspended (required on iOS)
      if (this.ctx.state === 'suspended') {
        await this.ctx.resume();
      }
      
      // Create a silent oscillator to fully unlock the audio context
      const oscillator = this.ctx.createOscillator();
      const gainNode = this.ctx.createGain();
      
      // Very low gain to be essentially silent
      gainNode.gain.value = 0.0001;
      
      oscillator.connect(gainNode);
      gainNode.connect(this.ctx.destination);
      
      oscillator.start();
      
      // Play for 150ms
      await new Promise(resolve => setTimeout(resolve, 150));
      
      oscillator.stop();
      
      // Mark as unlocked
      this.unlocked = true;
      sessionStorage.setItem('media_unlocked', '1');
      
      console.log('Media successfully unlocked for this session');
    } catch (error) {
      console.error('Failed to unlock media:', error);
      // Mark as unlocked anyway to avoid blocking
      sessionStorage.setItem('media_unlocked', '1');
      this.unlocked = true;
    }
  }
}
